import { format } from "date-fns";
import { MessageSquare, Check, RotateCcw, Trash2, MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useScheduleFeedback, type ScheduleFeedbackItem } from "@/hooks/useScheduleFeedback";
import type { TimeFormatter } from "@/hooks/useTimeFormatter";
import { useSchedulePanelHeight } from "@/hooks/useSchedulePanelHeight";
import { ResizablePanelSection } from "@/components/schedule/ResizablePanelSection";
import { DEFAULT_FEEDBACK_HEIGHT, PANEL_FEEDBACK } from "@/lib/schedulePanelHeights";

interface ScheduleFeedbackPanelProps {
  productionId: string;
  /** Clicking an anchored item asks the host to locate it on the schedule. */
  onFocusItem?: (item: ScheduleFeedbackItem) => void;
  /** Renders anchor times; comes from the schedule page that hosts this. */
  formatTime: TimeFormatter;
}

/**
 * Producer-facing list of client feedback for a production's schedule. Shown on
 * the schedule page so feedback is visible right where the producer works.
 * Backed by the shared useScheduleFeedback hook (so its counts match the nav
 * badge and dashboard widget) and live-updates via Realtime.
 */
export default function ScheduleFeedbackPanel({
  productionId,
  onFocusItem,
  formatTime,
}: ScheduleFeedbackPanelProps) {
  const { feedback, openCount, unreadCount, isLoading, markRead, resolve, reopen, remove } =
    useScheduleFeedback(productionId);
  const panel = useSchedulePanelHeight(productionId, PANEL_FEEDBACK, DEFAULT_FEEDBACK_HEIGHT);

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="h-5 w-5" />
          Client feedback
          {openCount > 0 && <Badge variant="destructive">{openCount} open</Badge>}
          {unreadCount > 0 && <Badge variant="default">{unreadCount} new</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="py-3 text-sm text-muted-foreground">Loading…</p>
        ) : feedback.length === 0 ? (
          <p className="py-3 text-sm text-muted-foreground">
            No client feedback yet. Feedback appears here as soon as a client leaves a comment via a
            share link.
          </p>
        ) : (
          // Scrolls once the list outgrows the panel, so it never pushes the
          // rest of the page away. The producer sets how tall that is.
          <ResizablePanelSection panel={panel} label="feedback list">
            <ul
              className="space-y-3 overflow-y-auto pr-1"
              style={{ maxHeight: `${panel.height}px` }}
            >
              {feedback.map((item) => (
                <FeedbackRow
                  formatTime={formatTime}
                  key={item.id}
                  item={item}
                  onRead={() => markRead(item.id)}
                  onResolve={() => resolve(item.id)}
                  onReopen={() => reopen(item.id)}
                  onRemove={() => remove(item.id)}
                  onFocus={
                    onFocusItem && item.stage_name && item.anchor_time
                      ? () => onFocusItem(item)
                      : undefined
                  }
                />
              ))}
            </ul>
          </ResizablePanelSection>
        )}
      </CardContent>
    </Card>
  );
}

function FeedbackRow({
  item,
  onRead,
  onResolve,
  onReopen,
  onRemove,
  onFocus,
  formatTime,
}: {
  item: ScheduleFeedbackItem;
  onRead: () => void;
  onResolve: () => void;
  onReopen: () => void;
  onRemove: () => void;
  onFocus?: () => void;
  formatTime: TimeFormatter;
}) {
  const resolved = item.status === "resolved";
  // Where on the schedule this feedback points (if anchored to a clicked spot).
  const anchorParts = [
    item.stage_name,
    item.anchor_time ? formatTime(item.anchor_time) : null,
    item.artist_name,
  ].filter(Boolean);
  return (
    <li
      className={`rounded-md border p-3 ${
        !item.is_read ? "border-primary/40 bg-primary/5" : "border-border"
      } ${onFocus ? "cursor-pointer transition-colors hover:bg-muted/40" : ""}`}
      onClick={onFocus}
      title={onFocus ? "Show on schedule" : undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{item.author_name}</span>
            {!item.is_read && (
              <Badge variant="default" className="text-xs">
                New
              </Badge>
            )}
            {resolved && (
              <Badge variant="outline" className="text-xs">
                Resolved
              </Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {format(new Date(item.created_at), "d MMM yyyy, HH:mm")}
            {item.event_date && ` · ${format(new Date(item.event_date), "EEEE d MMM")}`}
            {item.crew_members?.name && ` · ${item.crew_members.name}`}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {!item.is_read && (
            <Button variant="ghost" size="sm" onClick={onRead}>
              Mark read
            </Button>
          )}
          {resolved ? (
            <Button variant="ghost" size="sm" onClick={onReopen} title="Reopen">
              <RotateCcw className="h-4 w-4" />
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={onResolve} title="Mark as resolved">
              <Check className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={onRemove}
            title="Delete feedback"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {anchorParts.length > 0 && (
        <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" />
          {anchorParts.join(" · ")}
        </div>
      )}
      <p className="mt-2 whitespace-pre-wrap text-sm">{item.body}</p>
    </li>
  );
}
